// PUBLIC catalogue: safe to ship to the browser (titles, levels, timings, objectives, tags).
// Full lesson text lives in content/handbook.json and is served only by authenticated functions.
import data from "./catalog.json";

export type SectionId = "A" | "B" | "C" | "D" | "E" | "F";

export interface CatalogLesson {
  id: string;
  section: SectionId;
  sectionName: string;
  number: number;
  topic: string;
  activity: string;
  level: string;
  mixedLevel: boolean;
  levelMin: string;
  levelMax: string;
  levelIndex: [number, number];
  duration: number;
  durationLabel: string;
  skill: string;
  objective: string;
  tags: string[];
}
/** Full lesson (server-provided). */
export interface Lesson extends CatalogLesson {
  materials: string;
  warmup: string;
  main: string;
  practice: string;
  wrapup: string;
  differentiation: string;
  extension: string;
}
export interface PrincipleMeta {
  id: string;
  title: string;
  chapter: number;
}
export interface Principle extends PrincipleMeta {
  body: string[];
}
export interface Rubric {
  name: string;
  bands: string[];
  criteria: { criterion: string; descriptors: string[] }[];
}

interface Catalog {
  book: { title: string; subtitle: string; author: string; edition: string; year: number };
  sections: { id: SectionId; name: string }[];
  principles: PrincipleMeta[];
  rubrics: { name: string }[];
  lessons: CatalogLesson[];
}
export const catalog = data as unknown as Catalog;
export const lessons: CatalogLesson[] = catalog.lessons;
export const principles: PrincipleMeta[] = catalog.principles;
export const PRINCIPLE_IDS = principles.map((p) => p.id);

const byId = new Map(lessons.map((l) => [l.id.toUpperCase(), l]));
export const getCatalogLesson = (id: string | undefined | null) => (id ? byId.get(String(id).toUpperCase()) : undefined);
export const getPrinciple = (id: string) => principles.find((p) => p.id === id);

export const CEFR = ["Pre-A1", "A1", "A2", "B1", "B2", "C1"] as const;
export type Cefr = (typeof CEFR)[number];

export const SECTION_NAMES: Record<SectionId, string> = {
  A: "Grammar",
  B: "Vocabulary",
  C: "Reading",
  D: "Writing",
  E: "Listening & Speaking",
  F: "Assessment & Review",
};

/** Our own one-line summaries of Part I (not book text): used to ground the AI and preview principles. */
export const PRINCIPLE_BRIEFS: Record<string, string> = {
  "1.1": "Backward design: one measurable 'can do' outcome first; every activity is a step toward it.",
  "1.2": "PPP is the default; skills lessons may suit a task-based cycle instead.",
  "1.3": "Scaffold inside the ZPD with frames, word banks, models; withdraw support gradually.",
  "1.4": "Differentiate input, support, or output within one shared activity; stretch fast finishers.",
  "1.5": "Teens engage through games, competition, mystery, and talking about themselves.",
  "2.1": "Explicit, rehearsed routines for attention signals, grouping, and early finishers.",
  "2.2": "Large classes: peer models, self-checking, purposeful circulation, activities that spread attention.",
  "2.3": "English by default; brief, deliberate L1 when it saves more time than it costs.",
  "2.4": "Low-anxiety culture: no mocking errors; rehearse in pairs before whole-class production.",
  "2.5": "Choose pairs, triads, or jigsaw deliberately for the task; vary grouping across a unit.",
  "2.6": "Announced timings, visible timer, prepared transitions; tech only where it beats low-tech.",
};
